import ReactPlayer from 'react-player';
import { isAudioFile, isFileTypeImage } from '../constants/fileUtils';
import { getUrlMetaImageSources, getUrlMetaOriginalVideoUrl, getUrlMetaVideoSource, isUrlMetaVideo, isVideoLikeMediaType } from './url-meta-media';
import { getYouTubeVideoId } from './youtubeVideoId';
import { buildWorkspaceMediaGallery } from './workspaceMediaGallery';
import { getWorkspaceUrlMetaEntries } from './workspaceUrlMeta';

const httpUrl = source => {
    if (typeof source !== 'string') return '';
    try {
        return /^https?:$/.test(new URL(source).protocol) ? source : '';
    } catch (_) {
        return '';
    }
};

const directMediaKind = source => {
    try {
        const path = new URL(source).pathname;
        if (/\.(mp4|webm|mov|m4v|ogv|m3u8|mpd)$/i.test(path)) return 'video';
        if (/\.(mp3|m4a|aac|wav|ogg|oga|opus|flac)$/i.test(path)) return 'audio';
    } catch (_) { /* Not a direct media URL. */ }
    return '';
};

const providerMediaKind = source => {
    if (!httpUrl(source)) return '';
    if (getYouTubeVideoId(source)) return 'video';
    const host = new URL(source).hostname.toLowerCase().replace(/^www\./, '');
    // Provider pages are playable only when the installed player supports them.
    if (['vimeo.com', 'player.vimeo.com', 'dailymotion.com', 'dai.ly', 'twitch.tv',
        'clips.twitch.tv', 'facebook.com', 'fb.watch', 'streamable.com',
        'wistia.com', 'fast.wistia.net', 'soundcloud.com', 'snd.sc'].includes(host) && ReactPlayer.canPlay(source)) {
        return ['soundcloud.com', 'snd.sc'].includes(host) ? 'audio' : 'video';
    }
    return directMediaKind(source);
};

export const isSocialVideoPage = source => {
    if (!httpUrl(source)) return false;
    const { hostname, pathname } = new URL(source);
    const host = hostname.toLowerCase().replace(/^www\./, '');
    return ['instagram.com', 'instagr.am'].includes(host) && /^\/(reels?|tv)\//i.test(pathname) ||
        ['tiktok.com', 'm.tiktok.com'].includes(host) && /^\/@[^/]+\/video\//i.test(pathname);
};

export const isPdfAttachment = file => Boolean(file && (
    [file.type, file.mime].some(value => String(value || '').toLowerCase().split(';')[0].trim() === 'application/pdf') ||
    String(file.format || '').toLowerCase().replace(/^\./, '') === 'pdf' ||
    [file.name, file.displayName].some(value => /\.pdf$/i.test(String(value || '')))
));

export const getPdfWorkspaceSource = message => {
    const file = message?.file;
    const src = file?.cdn_url || file?.url || file?.original;
    return isPdfAttachment(file) && src ? { type: 'pdf', src, attachmentKind: 'file', storageKey: file.name } : null;
};

export const getAudioWorkspaceSource = message => {
    const file = message?.file;
    const src = file?.cdn_url || file?.url || file?.original;
    return isAudioFile(file) && src ? { type: 'audio', src } : null;
};

export const getUrlVideoWorkspaceSource = meta => {
    if (!meta) return null;
    const youtubeUrl = [meta.url, meta.media_url, meta.embed].find(source => getYouTubeVideoId(source));
    const mediaSource = getUrlMetaVideoSource(meta);
    const originalMediaSource = getUrlMetaOriginalVideoUrl(meta);
    const providerSource = [meta.url, meta.embed].find(source => providerMediaKind(source));
    const isAudio = /^audio(?:\/|$)/i.test(String(meta.media_type || ''));
    let src = youtubeUrl || (isAudio || isUrlMetaVideo(meta) || directMediaKind(mediaSource) ? mediaSource : '') ||
        providerSource ||
        ((isVideoLikeMediaType(meta.media_type) || isSocialVideoPage(meta.url)) ? (originalMediaSource || (directMediaKind(originalMediaSource) ? originalMediaSource : '')) : '');
    if (!src) return null;
    if (youtubeUrl && !ReactPlayer.canPlay(youtubeUrl)) {
        const original = new URL(youtubeUrl);
        const watch = new URL('https://www.youtube.com/watch');
        watch.searchParams.set('v', getYouTubeVideoId(youtubeUrl));
        for (const key of ['t', 'start']) {
            if (original.searchParams.has(key)) watch.searchParams.set(key, original.searchParams.get(key));
        }
        src = watch.toString();
    }
    return {
        type: isAudio ? 'audio' : providerMediaKind(src) || 'video',
        src,
        hlsUrl: meta.media_hls_url || '',
        originalUrl: meta.url || src,
        url: meta.url || src
    };
};

// Resolve the content, never the poster, as the destination of a Cards click.
export const getMessageWorkspaceSource = message => {
    const file = message?.file;
    const fileSource = file?.cdn_url || file?.url || file?.original;
    const attachmentImageSource = media => typeof media === 'string' ? media :
        media?.cdn_url || media?.url || (typeof media?.thumbnail === 'string' ? media.thumbnail :
            media?.thumbnail?.lg || media?.thumbnail?.xl || media?.thumbnail?.md || media?.thumbnail?.sm || media?.thumbnail?.original);
    const image = message?.image;
    const imageSource = attachmentImageSource(image);
    if (imageSource) return { type: 'image', src: imageSource, image, originalUrl: imageSource };
    if ([file?.type, file?.mime].some(isFileTypeImage)) {
        const src = attachmentImageSource(file);
        if (src) return { type: 'image', src, image: file, originalUrl: src };
    }
    const pdf = getPdfWorkspaceSource(message);
    if (pdf) return pdf;
    const audio = getAudioWorkspaceSource(message);
    if (audio) return audio;
    if (file && !isAudioFile(file) && [file.type, file.mime, file.format, file.name, file.displayName].some(isVideoLikeMediaType)) {
        return { type: 'video', src: fileSource || '', originalUrl: fileSource || '', url: fileSource || '' };
    }

    let previewImageSource = null;
    // Look through every saved link for playable/readable content before its cover.
    for (const { meta, workspaceUrlMetaIndex: index, workspaceQuotedMeta } of getWorkspaceUrlMetaEntries(message)) {
        const quoteIdentity = workspaceQuotedMeta ? { workspaceQuotedMeta: true } : {};
        const video = getUrlVideoWorkspaceSource(meta);
        // Provider extraction may save a YouTube cover as a one-image gallery.
        if (video && getYouTubeVideoId(video.src)) return { ...video, workspaceUrlMetaIndex: index, ...quoteIdentity };
        const mediaType = String(meta.media_type || '').toLowerCase();
        const gallery = (Array.isArray(meta.gallery) ? meta.gallery : []).filter(slide => slide && typeof slide === 'object');
        const workspaceGallery = buildWorkspaceMediaGallery(gallery, { originalUrl: meta.url, title: meta.title });
        const isImageContent = ['image', 'carousel'].includes(mediaType) && !isSocialVideoPage(meta.url);
        if (video && gallery.length <= 1) return { ...video, workspaceUrlMetaIndex: index, ...quoteIdentity };
        if (!video && String(meta.article_body || '').trim() && mediaType !== 'carousel') {
            return { type: 'article', articleBody: meta.article_body, title: meta.title,
                url: meta.article_url || meta.url, workspaceUrlMetaIndex: index, ...quoteIdentity };
        }
        // A generic link's single cover is not a saved photo carousel.
        if (gallery.length && (gallery.length > 1 || isImageContent || workspaceGallery.some(slide => slide.type === 'video'))) {
            return {
                type: 'image',
                gallery: workspaceGallery,
                initialIndex: 0,
                workspaceUrlMetaIndex: index, ...quoteIdentity
            };
        }
        if (video) return { ...video, workspaceUrlMetaIndex: index, ...quoteIdentity };
        if (String(meta.article_body || '').trim()) {
            return { type: 'article', articleBody: meta.article_body, title: meta.title,
                url: meta.article_url || meta.url, workspaceUrlMetaIndex: index, ...quoteIdentity };
        }
        const legacyGalleryImages = (Array.isArray(meta.gallery_images) ? meta.gallery_images : []).filter(image => typeof image === 'string' && image);
        if (legacyGalleryImages.length > 1 || isImageContent && legacyGalleryImages.length) {
            return { type: 'image', gallery: buildWorkspaceMediaGallery(
                legacyGalleryImages.map(image => ({ type: 'image', image })),
                { originalUrl: meta.url, title: meta.title }
            ), initialIndex: 0, workspaceUrlMetaIndex: index, ...quoteIdentity };
        }
        let directImage = false;
        try { directImage = /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(new URL(meta.url).pathname); } catch (_) { /* Not a direct image link. */ }
        if (directImage || isImageContent) {
            const src = getUrlMetaImageSources(meta).find(Boolean) || (directImage ? httpUrl(meta.url) : '');
            if (src) return { type: 'image', src, originalUrl: meta.url, workspaceUrlMetaIndex: index, ...quoteIdentity };
        }
        // Legacy photo posts may have no media_type or only one gallery image.
        // Keep their image as a fallback without masking a later video/article.
        if (!previewImageSource && !isSocialVideoPage(meta.url) && !isVideoLikeMediaType(meta.media_type)) {
            const src = getUrlMetaImageSources(meta).find(Boolean) || workspaceGallery[0]?.src;
            if (src) previewImageSource = { type: 'image', src, originalUrl: meta.url, workspaceUrlMetaIndex: index, ...quoteIdentity };
        }
    }
    if (previewImageSource) return previewImageSource;
    // Generic files keep their attachment renderer (including PDF), not an img.
    if (file) return { type: 'message' };
    // Links without playable/saved content still open their full in-app preview.
    return { type: 'message' };
};

export const needsSourceFolderForMessageLink = (viewFolder, message, viewerId) => {
    if (!viewFolder?.isStack && !viewFolder?.isType) return false;
    const sourceOwner = String(message?.folder?.owner || '');
    return !viewerId || !sourceOwner || sourceOwner !== String(viewerId);
};

export const shouldRestoreMessageWorkspace = ({ message, isSearchList, search, restoredMessageId }) => {
    if (isSearchList || message?.previewMode !== 'workspace' || !message?.id) return false;
    const params = new URLSearchParams(search);
    return params.get('workspace') === '1' && params.get('m') === String(message.id) &&
        restoredMessageId !== String(message.id);
};

export const syncWorkspaceUrl = (messageId, isOpen) => {
    if (typeof window === 'undefined') {
        return;
    }

    const id = String(messageId || '').trim();
    const url = new URL(window.location.href);
    if (isOpen && id) {
        if (url.searchParams.get('m') === id && url.searchParams.get('workspace') === '1') {
            return;
        }
        url.searchParams.set('m', id);
        url.searchParams.set('workspace', '1');
        window.history.pushState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
        return;
    }

    if (url.searchParams.has('workspace')) {
        url.searchParams.delete('workspace');
        window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    }
};

export const isPlainTitleAndNoteMessage = message => {
    if (!message) return false;
    if (message.file || message.image) return false;
    if (Array.isArray(message.urlMeta) && message.urlMeta.some(Boolean)) return false;
    if (Array.isArray(message.artifacts) && message.artifacts.some(Boolean)) return false;
    if (Array.isArray(message.entities) && message.entities.some(Boolean)) return false;
    return true;
};

